import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Youtube,
  CheckCircle2,
  XCircle,
  ExternalLink,
  Clock,
  Lock,
  Globe,
  Eye,
  Calendar as CalendarIcon,
} from "lucide-react";

const VISIBILITY_OPTIONS = [
  { v: "unlisted", label: "Unlisted", icon: Eye,
    desc: "Anyone with the link can watch. Default for safety." },
  { v: "public", label: "Public", icon: Globe,
    desc: "Anyone can search and watch." },
  { v: "private", label: "Private", icon: Lock,
    desc: "Only you can watch (use 'Schedule' below for timed release)." },
];

function statusBadge(s) {
  const map = {
    uploading: { cls: "bg-amber-500", label: "uploading", icon: Loader2 },
    published: { cls: "bg-emerald-600", label: "published", icon: CheckCircle2 },
    scheduled: { cls: "bg-sky-600", label: "scheduled", icon: Clock },
    failed:    { cls: "bg-red-600",     label: "failed",    icon: XCircle },
    idle:      { cls: "bg-stone-400",   label: "idle",      icon: Eye },
  };
  return map[s] || map.idle;
}

export default function PublishDialog({ video, ytConnected, onClose }) {
  const [title, setTitle] = useState(video.title || "");
  const [description, setDescription] = useState(video.description || "");
  const [tagsText, setTagsText] = useState((video.tags || []).join(", "));
  const [visibility, setVisibility] = useState("unlisted");
  const [madeForKids, setMadeForKids] = useState(true);
  const [schedule, setSchedule] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");
  const [playlists, setPlaylists] = useState([]);
  const [playlistId, setPlaylistId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [job, setJob] = useState(video.publish_status || null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [thumbBust, setThumbBust] = useState(0);
  const [regenLoading, setRegenLoading] = useState(false);
  const [uploadSrt, setUploadSrt] = useState(true);
  // Phase 5.7 publish polish — pin comment defaults
  const [pinComment, setPinComment] = useState(true);
  const [commentText, setCommentText] = useState(
    "Like & Subscribe to Dom's Journey for more fun videos! 🎵",
  );

  const pollRef = useRef(null);

  useEffect(() => {
    if (!ytConnected) return;
    api
      .get("/youtube/oauth/playlists")
      .then((r) => setPlaylists(r.data.items || []))
      .catch(() => {});
  }, [ytConnected]);

  useEffect(() => {
    return () => clearInterval(pollRef.current);
  }, []);

  const startPoll = () => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/videos/${video.id}/publish/status`);
        setJob(r.data);
        if (["published", "scheduled", "failed"].includes(r.data.status)) {
          clearInterval(pollRef.current);
        }
      } catch {
        /* ignore */
      }
    }, 1500);
  };

  const submit = async () => {
    setErrorMsg(null);
    if (schedule && !scheduleAt) {
      setErrorMsg("Pick a schedule date/time or turn off scheduling.");
      return;
    }
    setSubmitting(true);
    try {
      const tags = tagsText
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean)
        .slice(0, 30);
      const payload = {
        privacy_status: visibility,
        made_for_kids: madeForKids,
        publish_at: schedule
          ? new Date(scheduleAt).toISOString()
          : null,
        playlist_id: playlistId || null,
        title_override: title.slice(0, 100),
        description_override: description.slice(0, 5000),
        tags_override: tags,
        upload_srt: uploadSrt,
        pin_comment: pinComment,
        comment_text: pinComment ? commentText.slice(0, 500) : null,
      };
      const r = await api.post(`/videos/${video.id}/publish`, payload);
      setJob(r.data);
      startPoll();
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const isUploading = job?.status === "uploading";
  const isDone = ["published", "scheduled"].includes(job?.status);
  const Sb = statusBadge(job?.status);

  // Min schedule = 15 minutes from now (YouTube requirement is 15s but lets
  // give the user a sane default)
  const minSchedule = useMemo(() => {
    const d = new Date(Date.now() + 15 * 60 * 1000);
    d.setSeconds(0, 0);
    return d.toISOString().slice(0, 16);
  }, []);

  return (
    <div
      data-testid="publish-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/60 p-4"
      onClick={onClose}
    >
      <div
        data-testid="publish-section"
        className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Youtube className="h-6 w-6 text-red-600" />
            <h2 className="text-xl font-bold text-stone-800">
              Publish to YouTube
            </h2>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            data-testid="publish-close"
          >
            Close
          </Button>
        </div>

        {/* OAuth status — always rendered. Form below is also always
            rendered; the publish button is disabled until OAuth connects. */}
        {!ytConnected ? (
          <div
            data-testid="publish-oauth-status"
            className="mb-4 rounded-lg border border-dashed border-red-300 bg-red-50/50 p-4 text-sm text-red-800"
          >
            <div className="mb-2 font-semibold">
              YouTube isn't connected yet.
            </div>
            <Button
              data-testid="publish-oauth-connect"
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={async () => {
                try {
                  const r = await api.get("/youtube/oauth/authorize");
                  const url = r?.data?.authorize_url || r?.data?.url;
                  if (url) window.open(url, "_blank", "noopener");
                } catch (e) {
                  setErrorMsg(
                    e?.response?.data?.detail
                      || "Could not start OAuth flow",
                  );
                }
              }}
            >
              <Youtube className="mr-2 h-4 w-4" />
              Connect YouTube
            </Button>
            <div className="mt-2 text-xs text-red-700/80">
              Opens a Google consent page in a new tab. After approval,
              close that tab and reopen this dialog. The Publish button is
              locked until OAuth is connected.
            </div>
          </div>
        ) : (
          <div
            data-testid="publish-oauth-status"
            className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800"
          >
            ✓ YouTube connected
            {video.youtube_channel_title
              ? ` as ${video.youtube_channel_title}`
              : ""}
            {" — ready to publish."}
          </div>
        )}

        {isDone ? (
          <div className="space-y-4">
            <div
              data-testid="publish-success"
              className="flex items-start gap-3 rounded-lg border border-emerald-300 bg-emerald-50 p-4"
            >
              <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600" />
              <div className="flex-1">
                <div className="font-semibold text-emerald-900">
                  {job.status === "scheduled"
                    ? "Scheduled on YouTube"
                    : "Published to YouTube"}
                </div>
                <div className="mt-1 text-sm text-emerald-800">
                  Visibility: <strong>{job.privacy_status}</strong> · Made for
                  Kids: <strong>{job.made_for_kids ? "Yes" : "No"}</strong>
                  {job.publish_at ? (
                    <>
                      {" "}
                      · Goes live:{" "}
                      <strong>
                        {new Date(job.publish_at).toLocaleString()}
                      </strong>
                    </>
                  ) : null}
                </div>
                {job.youtube_url ? (
                  <a
                    data-testid="youtube-link"
                    href={job.youtube_url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 underline"
                  >
                    {job.youtube_url}{" "}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ) : null}
              </div>
            </div>
          </div>
        ) : isUploading ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-stone-700">
              <Loader2 className="h-4 w-4 animate-spin text-orange-500" />
              <span className="text-sm font-medium">
                Uploading to YouTube…
              </span>
              <Badge className={Sb.cls}>{Sb.label}</Badge>
            </div>
            <div className="h-3 w-full overflow-hidden rounded-full bg-stone-100">
              <div
                data-testid="publish-progress"
                className="h-full bg-gradient-to-r from-orange-500 to-rose-500 transition-all"
                style={{ width: `${job.progress || 0}%` }}
              />
            </div>
            <div className="text-xs text-stone-500">
              {job.progress || 0}% — uploads can take 30s–2 min depending on
              file size and your connection. You can leave this dialog open.
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Thumbnail preview + regenerate */}
            <div
              data-testid="publish-thumbnail-select"
              className="rounded-lg border border-orange-100 bg-orange-50/30 p-3"
            >
              <div className="mb-2 flex items-center justify-between">
                <div className="text-sm font-medium text-stone-800">
                  YouTube thumbnail
                  <span className="ml-2 text-xs font-normal text-stone-500">
                    1280×720 · auto-uploaded with the video
                  </span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="thumbnail-regenerate"
                  disabled={regenLoading}
                  onClick={async () => {
                    setRegenLoading(true);
                    try {
                      await api.post(
                        `/videos/${video.id}/thumbnail/regenerate`,
                      );
                      setThumbBust(Date.now());
                    } catch (e) {
                      setErrorMsg(e?.response?.data?.detail || e.message);
                    } finally {
                      setRegenLoading(false);
                    }
                  }}
                >
                  {regenLoading ? (
                    <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                  ) : null}
                  Regenerate
                </Button>
              </div>
              <img
                data-testid="thumbnail-preview"
                src={`${
                  process.env.REACT_APP_BACKEND_URL || ""
                }/api/videos/${video.id}/thumbnail/custom.png?t=${thumbBust}`}
                alt="thumbnail preview"
                className="aspect-video w-full rounded-md border border-orange-200 object-cover"
                onError={(e) => {
                  e.currentTarget.style.opacity = "0.4";
                }}
              />
              <input
                data-testid="thumbnail-upload"
                type="file"
                accept="image/png,image/jpeg"
                className="mt-2 block w-full text-xs text-stone-600"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  const fd = new FormData();
                  fd.append("file", f);
                  setRegenLoading(true);
                  try {
                    await api.post(
                      `/videos/${video.id}/thumbnail/upload`, fd,
                      { headers: { "Content-Type": "multipart/form-data" } },
                    );
                    setThumbBust(Date.now());
                  } catch (err) {
                    setErrorMsg(err?.response?.data?.detail || err.message);
                  } finally {
                    setRegenLoading(false);
                  }
                }}
              />
            </div>

            {/* Title / description / tags */}
            <div>
              <label className="mb-1 block text-sm font-medium text-stone-700">
                Title <span className="text-xs text-stone-400">
                  ({title.length}/100)
                </span>
              </label>
              <Input
                data-testid="publish-title-input"
                id="publish-title"
                value={title}
                onChange={(e) => setTitle(e.target.value.slice(0, 100))}
                className="border-orange-200"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-stone-700">
                Description{" "}
                <span className="text-xs text-stone-400">
                  ({description.length}/5000)
                </span>
              </label>
              <textarea
                data-testid="publish-description-textarea"
                id="publish-description"
                value={description}
                onChange={(e) => setDescription(e.target.value.slice(0, 5000))}
                rows={5}
                className="w-full rounded-md border border-orange-200 bg-white px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-stone-700">
                Tags <span className="text-xs text-stone-400">
                  (comma-separated, max 30)
                </span>
              </label>
              <Input
                data-testid="publish-tags-input"
                id="publish-tags"
                value={tagsText}
                onChange={(e) => setTagsText(e.target.value)}
                className="border-orange-200"
              />
            </div>

            {/* Visibility */}
            <div data-testid="publish-visibility-select">
              <label className="mb-1 block text-sm font-medium text-stone-700">
                Visibility
              </label>
              <div className="grid gap-2 sm:grid-cols-3">
                {VISIBILITY_OPTIONS.map((o) => {
                  const Icon = o.icon;
                  const active = visibility === o.v;
                  return (
                    <button
                      type="button"
                      key={o.v}
                      data-testid={`visibility-${o.v}`}
                      onClick={() => setVisibility(o.v)}
                      className={`rounded-lg border p-3 text-left transition ${
                        active
                          ? "border-orange-500 bg-orange-50 shadow-sm"
                          : "border-stone-200 bg-white hover:border-orange-300"
                      }`}
                    >
                      <div className="flex items-center gap-2 text-sm font-semibold text-stone-800">
                        <Icon className="h-4 w-4" />
                        {o.label}
                      </div>
                      <div className="mt-1 text-xs text-stone-500">
                        {o.desc}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Made for kids */}
            <div className="flex items-center justify-between rounded-lg border border-orange-100 bg-orange-50/40 px-4 py-3">
              <div>
                <div className="text-sm font-medium text-stone-800">
                  Made for Kids (COPPA)
                </div>
                <div className="text-xs text-stone-600">
                  Required for content directed at children. Disables
                  personalised ads + comments on this video.
                </div>
              </div>
              <Switch
                data-testid="publish-made-for-kids-checkbox"
                id="made-for-kids"
                checked={madeForKids}
                onCheckedChange={setMadeForKids}
              />
            </div>

            {/* Pin pinned comment */}
            <div
              data-testid="publish-pin-comment-row"
              className="rounded-lg border border-orange-100 bg-white px-4 py-3"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-stone-800">
                    Pin a comment after publish
                  </div>
                  <div className="text-xs text-stone-600">
                    Auto-posts and pins the message below to the top of the
                    video's comments thread.
                  </div>
                </div>
                <Switch
                  data-testid="publish-pin-comment-checkbox"
                  checked={pinComment}
                  onCheckedChange={setPinComment}
                />
              </div>
              {pinComment ? (
                <textarea
                  data-testid="publish-pin-comment-text"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value.slice(0, 500))}
                  rows={2}
                  className="mt-2 w-full rounded-md border border-orange-200 bg-white px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
                />
              ) : null}
            </div>

            {/* Schedule */}
            <div className="rounded-lg border border-orange-100 bg-white px-4 py-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 text-sm font-medium text-stone-800">
                    <CalendarIcon className="h-4 w-4" />
                    Schedule for later
                  </div>
                  <div className="text-xs text-stone-600">
                    Uploads as <em>private</em> now and goes live at the
                    chosen time.
                  </div>
                </div>
                <Switch
                  data-testid="schedule-toggle"
                  checked={schedule}
                  onCheckedChange={setSchedule}
                />
              </div>
              {schedule ? (
                <Input
                  type="datetime-local"
                  data-testid="schedule-at"
                  value={scheduleAt}
                  min={minSchedule}
                  onChange={(e) => setScheduleAt(e.target.value)}
                  className="mt-2 border-orange-200"
                />
              ) : null}
            </div>

            {/* Playlist */}
            {playlists.length > 0 ? (
              <div>
                <label className="mb-1 block text-sm font-medium text-stone-700">
                  Add to playlist (optional)
                </label>
                <select
                  data-testid="playlist-select"
                  value={playlistId}
                  onChange={(e) => setPlaylistId(e.target.value)}
                  className="w-full rounded-md border border-orange-200 bg-white px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
                >
                  <option value="">— None —</option>
                  {playlists.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title} ({p.video_count})
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            {errorMsg ? (
              <div
                data-testid="publish-error"
                className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                {errorMsg}
              </div>
            ) : null}

            {job?.status === "failed" && job.error ? (
              <div
                data-testid="publish-prev-error"
                className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700"
              >
                Last attempt failed: {job.error}
              </div>
            ) : null}

            {/* Upload .srt to YouTube as CC */}
            <div
              data-testid="publish-srt-toggle-row"
              className={`flex items-start gap-3 rounded-lg border px-3 py-2 text-sm transition ${
                video.srt_url
                  ? "border-orange-200 bg-orange-50/50"
                  : "border-stone-200 bg-stone-50 opacity-70"
              }`}
            >
              <Switch
                data-testid="publish-srt-toggle"
                checked={video.srt_url ? uploadSrt : false}
                onCheckedChange={(v) =>
                  video.srt_url ? setUploadSrt(!!v) : null
                }
                disabled={!video.srt_url}
              />
              <div className="flex-1">
                <div className="font-medium text-stone-800">
                  Upload subtitles to YouTube (.srt)
                </div>
                <div className="text-xs text-stone-600">
                  {video.srt_url ? (
                    <>
                      Ships canonical lyric/narration captions as a YouTube
                      CC track. Recommended ON for accessibility, retention
                      on muted autoplay, and lyrics-in-search.{" "}
                      <a
                        href={video.srt_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="publish-srt-preview"
                        className="font-medium text-orange-700 underline-offset-2 hover:underline"
                      >
                        Preview .srt
                      </a>
                    </>
                  ) : (
                    "No .srt sidecar available for this video."
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={submitting || !title.trim() || !ytConnected}
                data-testid="publish-button"
                id="publish-submit"
                className="bg-red-600 text-white hover:bg-red-700"
              >
                {submitting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Youtube className="mr-2 h-4 w-4" />
                )}
                {schedule ? "Schedule on YouTube" : "Publish now"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
